import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, KeyRound } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';
import { supabase } from '../services/supabase';
import { playSound } from '../services/audioService';

interface Props {
    onDone: () => void;
}

export const PasswordResetScreen: React.FC<Props> = ({ onDone }) => {
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [hasRecoverySession, setHasRecoverySession] = useState(true);

    useEffect(() => {
        const verifySession = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) {
                setHasRecoverySession(false);
                setError(true);
                setErrorMessage('Link expirado ou inválido. Solicite novo reset.');
            }
        };
        verifySession();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(false);
        setErrorMessage(null);

        if (!hasRecoverySession) {
            setError(true);
            setErrorMessage('Sessão de recuperação inválida. Solicite novo reset.');
            toast.error('Sessão de recuperação inválida. Solicite novo reset.');
            return;
        }

        if (!password || password.length < 8) {
            setError(true);
            setErrorMessage('Use uma senha com pelo menos 8 caracteres.');
            toast.error('Use uma senha com pelo menos 8 caracteres.');
            return;
        }

        if (password !== confirm) {
            setError(true);
            setErrorMessage('As senhas não coincidem.');
            toast.error('As senhas não coincidem.');
            return;
        }

        setIsLoading(true);
        try {
            const { error: updateError } = await supabase.auth.updateUser({ password });
            if (updateError) throw updateError;

            toast.success('Senha atualizada. Faça login novamente.');
            playSound('success');
            await supabase.auth.signOut();
            if (typeof window !== 'undefined') window.location.hash = '';
            onDone();
        } catch (err) {
            console.error(err);
            playSound('error');
            setError(true);
            const message = err instanceof Error ? err.message : 'Falha ao atualizar a senha.';
            setErrorMessage(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-app-bg flex flex-col items-center justify-center p-4 relative overflow-hidden transition-colors duration-300">
            <Toaster position="top-center" containerStyle={{ zIndex: 100000 }} />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] dark:opacity-20 pointer-events-none"></div>

            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, x: error ? [0, -10, 10, -10, 10, 0] : 0 }}
                style={{
                    backgroundColor: 'rgba(var(--glass-panel-bg))',
                    backdropFilter: 'blur(32px)',
                    WebkitBackdropFilter: 'blur(32px)'
                }}
                className="z-10 w-full max-w-[420px] border border-border-dim p-8 rounded-[2.5rem] shadow-[var(--shadow-lg)] relative"
            >
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-card-bg p-4 rounded-3xl border border-border-dim shadow-xl">
                    <ShieldCheck className="w-10 h-10 text-sal-cyber-teal" />
                </div>

                <div className="mt-8 text-center mb-8">
                    <h1 className="text-3xl font-black text-text-primary tracking-tighter uppercase mb-1">S.A.L. Rota</h1>
                    <p className="text-xs text-text-muted uppercase tracking-[0.3em] font-black">REDEFINIR SENHA</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <div className="relative">
                            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-icon-muted" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-input-bg border border-border-dim pl-12 pr-4 py-4 rounded-2xl text-base focus:outline-none focus:border-sal-cyber-teal transition-all text-text-primary placeholder:text-text-muted/50"
                                placeholder="Nova senha"
                                required
                                disabled={isLoading}
                            />
                        </div>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-icon-muted" />
                            <input
                                type="password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="w-full bg-input-bg border border-border-dim pl-12 pr-4 py-4 rounded-2xl text-base focus:outline-none focus:border-sal-cyber-teal transition-all text-text-primary placeholder:text-text-muted/50"
                                placeholder="Confirmar senha"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-sal-cyber-teal text-badge-text-inv font-black py-4 rounded-2xl shadow-xl shadow-cyan-900/20 hover:brightness-110 active:scale-95 transition-all uppercase tracking-widest text-xs flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isLoading ? "Atualizando..." : "Definir nova senha"}
                    </button>
                </form>

                {errorMessage && (
                    <div className="mt-4 text-center text-xs font-bold uppercase tracking-widest text-sal-burnt-orange">
                        {errorMessage}
                    </div>
                )}

                <div className="mt-8 text-center">
                    <p className="text-xs text-text-muted font-bold uppercase leading-relaxed tracking-wider">
                        Atualize sua senha para continuar.
                    </p>
                </div>
            </motion.div>
        </div>
    );
};
